import { ChangeDetectorRef, Component, inject, Input } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatIcon } from '@angular/material/icon';
import { GlobalMetadataFactoryService } from '../../../services/global-metadata.factory.service';
import { MetadataFieldDataType } from '../../../../core/types';

@Component({
  selector: 'pixl-form-dialog-global-metadata-dropdown',
  templateUrl: './form-dialog-global-metadata.dropdown.component.html',
  imports: [
    CommonModule,
    MatFormField,
    ReactiveFormsModule,
    FormsModule,
    MatLabel,
    MatInput,
    MatButton,
    MatSelect,
    MatOption,
    MatIcon,
    MatIconButton
  ]
})
export class FormDialogGlobalMetadataDropdownComponent {
  @Input() parentGroup!: FormGroup;

  cdr = inject(ChangeDetectorRef);
  fb = inject(FormBuilder);

  private metaFactory = inject(GlobalMetadataFactoryService);

  // Options for datatype dropdown
  datatypeOptions = ['String', 'Int', 'Checkbox', 'ValueFromApiResult'];

  get options(): FormArray {
    return this.parentGroup.get('subFieldConf') as FormArray;
  }

  public getFormGroup(control: AbstractControl | null): FormGroup | null {
    return control && control instanceof FormGroup ? (control as FormGroup) : null;
  }

  public isFormGroup(control: AbstractControl | null): control is FormGroup {
    return control instanceof FormGroup;
  }

  public getSubFieldConf(group: FormGroup): FormArray {
    return group.get('subFieldConf') as FormArray;
  }

  public addSubField(group: FormGroup, datatype: MetadataFieldDataType = 'String') {
    const newGroup: FormGroup = this.metaFactory.createFieldConfGroup({
      name: '',
      label: '',
      datatype,
      subFieldConf: [],
    });

    const labelControl = newGroup.get('label') as FormControl<string>;
    const nameControl = newGroup.get('name') as FormControl<string>;

    if (labelControl && nameControl) {
      nameControl.setValue(this.metaFactory.toKey(labelControl.value ?? ''), { emitEvent: false });
      labelControl.valueChanges.subscribe((val: string | null) => {
        const key = this.metaFactory.toKey(val ?? '');
        if (key !== nameControl.value) {
          nameControl.setValue(key, { emitEvent: false });
        }
      });
    }

    setTimeout(() => {
      this.getSubFieldConf(group).push(newGroup);
      this.cdr.detectChanges();
    }, 0);
  }

  public getDetailGroup(control: AbstractControl | null): FormGroup | null {
    if (!control) return null;
    return control.get('detail') as FormGroup | null;
  }

  public removeSubField(array: FormArray, index: number) {
    array.removeAt(index);
  }

  public getHeadersArray(control: AbstractControl | null): FormArray {
    const detail = this.getDetailGroup(control);
    if (!detail) return this.fb.array([]);
    const headers = detail.get('headers');
    return headers instanceof FormArray ? (headers as FormArray) : this.fb.array([]);
  }

  public addHeader(parent: AbstractControl | null) {
    if (!parent || !(parent instanceof FormGroup)) return;

    const headersArray = this.getHeadersArray(parent);

    headersArray.push(
      this.fb.group({
        name: [''],
        values: this.fb.array([this.fb.control('')])
      })
    );
  }

  public addHeaderValue(headerGroup: AbstractControl | null) {
    if (!headerGroup || !(headerGroup instanceof FormGroup)) return;

    const valuesArray = headerGroup.get('values');
    if (!valuesArray || !(valuesArray instanceof FormArray)) return;

    valuesArray.push(this.fb.control(''));
  }

  public getHeaderValuesArray(headerGroup: AbstractControl | null): FormArray {
    if (!headerGroup) return this.fb.array([]);
    const values = headerGroup.get('values');
    return values instanceof FormArray ? (values as FormArray) : this.fb.array([]);
  }

  public removeHeader(parent: AbstractControl | null, index: number) {
    const headersArray = this.getHeadersArray(parent);
    headersArray.removeAt(index);
  }

  public removeHeaderValue(headerGroup: AbstractControl | null, index: number) {
    if (!headerGroup || !(headerGroup instanceof FormGroup)) return;
    const values = headerGroup.get('values');
    if (!(values instanceof FormArray)) return;
    values.removeAt(index);
  }
}
